/**
 * useGuideManager — Convenience hook for controlling the guide system from any package.
 *
 * Wraps `useGlobalState('guides')` and exposes the slice state plus named functions
 * so consumers never need to import `useGlobalState('guides')` or know the internal
 * action type strings. This is the single recommended entry point for any package
 * that needs to read or control guide state.
 *
 * Usage:
 * ```tsx
 * const { openManager, startGuide, markDiscovered } = useGuideManager();
 * openManager(GuideId.bomOverview); // Opens the dialog preselected on BOM Overview
 * ```
 */
import { useCallback } from 'react';

import type { GuideId } from './guideId';
import type {
    GuideCompletionState,
    GuideDiscoveryState,
    GuideStepIndexState,
} from './guidesReducer';

import { useGlobalState } from '../../useGlobalState';

interface GuideManagerControls {
    /** Whether the guide manager dialog is currently open */
    isManagerOpen: boolean;
    /** The currently selected/highlighted guide in the manager dialog */
    selectedManagerGuideId: GuideId;
    /** The guide that is currently being played (null if no guide is active) */
    runningGuideId: GuideId | null;
    /** Per-guide completion state (true = the user has finished that guide) */
    completionByGuide: GuideCompletionState;
    /** Per-guide discovery state (true = the user has seen that guide, so its beacon hides) */
    discoveryByGuide: GuideDiscoveryState;
    /** Per-guide step index (drives controlled guides so any element can advance the tour) */
    stepIndexByGuide: GuideStepIndexState;
    /** Whether the user has already seen the "all guides completed" celebration */
    isCelebrationOver: boolean;
    /**
     * Sets the current step index for a guide, letting any page element drive a controlled tour.
     * @param guideId - The guide to update
     * @param stepIndex - The step index to move the guide to
     */
    setStepIndex: (guideId: GuideId, stepIndex: number) => void;
    /**
     * Opens the guide manager dialog.
     * @param guideId - Optional guide to preselect when the dialog opens.
     *                  If omitted, the previously selected guide remains selected.
     */
    openManager: (guideId?: GuideId) => void;
    /** Closes the guide manager dialog */
    closeManager: () => void;
    /**
     * Changes the selected/highlighted guide in the manager, without opening/closing it.
     * @param guideId - The guide to select
     */
    selectGuide: (guideId: GuideId) => void;
    /**
     * Starts playing a specific guide.
     * Sets runningGuideId and resets that guide's step index.
     * @param guideId - The guide to start
     */
    startGuide: (guideId: GuideId) => void;
    /** Stops the currently running guide without marking it as completed */
    stopGuide: () => void;
    /**
     * Marks the running guide as finished: sets its completion state and clears runningGuideId.
     * @param guideId - The guide that finished
     */
    finishGuide: (guideId: GuideId) => void;
    /**
     * Marks a guide as discovered, hiding its beacon.
     * @param guideId - The guide the user has seen
     */
    markDiscovered: (guideId: GuideId) => void;
    /**
     * Marks a guide as undiscovered, showing its beacon again.
     * @param guideId - The guide to reset
     */
    resetDiscovery: (guideId: GuideId) => void;
    /** Marks every guide as discovered */
    markAllDiscovered: () => void;
    /** Marks every guide as undiscovered */
    resetAllDiscovery: () => void;
    /**
     * Marks a guide as completed.
     * @param guideId - The guide the user has finished
     */
    markCompleted: (guideId: GuideId) => void;
    /**
     * Marks a guide as not completed.
     * @param guideId - The guide to reset
     */
    resetCompletion: (guideId: GuideId) => void;
    /** Marks every guide as completed */
    markAllCompleted: () => void;
    /** Marks every guide as not completed */
    resetAllCompletion: () => void;
    /** Marks the "all guides completed" celebration as seen, hiding it from playing again */
    markCelebrationOver: () => void;
    /** Resets the celebration so it plays again next time all guides are completed */
    resetCelebrationOver: () => void;
}

/**
 * Hook that provides the guides slice state plus functions to control the guide system.
 * Can be called from any package that has access to @buying/global-state.
 *
 * @returns The guides slice state and functions to control the guide system
 */
const useGuideManager = (): GuideManagerControls => {
    const [guidesState, dispatchGuides] = useGlobalState('guides');

    const openManager = useCallback(
        (guideId?: GuideId): void => {
            dispatchGuides({
                type: 'open_manager',
                ...(guideId !== undefined && { payload: { guideId } }),
            });
        },
        [dispatchGuides]
    );

    const closeManager = useCallback((): void => {
        dispatchGuides({ type: 'close_manager' });
    }, [dispatchGuides]);

    const selectGuide = useCallback(
        (guideId: GuideId): void => {
            dispatchGuides({ type: 'select_guide', payload: { guideId } });
        },
        [dispatchGuides]
    );

    const startGuide = useCallback(
        (guideId: GuideId): void => {
            dispatchGuides({ type: 'start_guide', payload: { guideId } });
        },
        [dispatchGuides]
    );

    const stopGuide = useCallback((): void => {
        dispatchGuides({ type: 'stop_guide' });
    }, [dispatchGuides]);

    const setStepIndex = useCallback(
        (guideId: GuideId, stepIndex: number): void => {
            dispatchGuides({
                type: 'set_step_index',
                payload: { guideId, stepIndex },
            });
        },
        [dispatchGuides]
    );

    const finishGuide = useCallback(
        (guideId: GuideId): void => {
            dispatchGuides({ type: 'complete_guide', payload: { guideId } });
        },
        [dispatchGuides]
    );

    const markDiscovered = useCallback(
        (guideId: GuideId): void => {
            dispatchGuides({
                type: 'set_discovered',
                payload: { guideId, isDiscovered: true },
            });
        },
        [dispatchGuides]
    );

    const resetDiscovery = useCallback(
        (guideId: GuideId): void => {
            dispatchGuides({
                type: 'set_discovered',
                payload: { guideId, isDiscovered: false },
            });
        },
        [dispatchGuides]
    );

    const markAllDiscovered = useCallback((): void => {
        dispatchGuides({
            type: 'set_all_discovered',
            payload: { isDiscovered: true },
        });
    }, [dispatchGuides]);

    const resetAllDiscovery = useCallback((): void => {
        dispatchGuides({
            type: 'set_all_discovered',
            payload: { isDiscovered: false },
        });
    }, [dispatchGuides]);

    const markCompleted = useCallback(
        (guideId: GuideId): void => {
            dispatchGuides({
                type: 'set_completed',
                payload: { guideId, isCompleted: true },
            });
        },
        [dispatchGuides]
    );

    const resetCompletion = useCallback(
        (guideId: GuideId): void => {
            dispatchGuides({
                type: 'set_completed',
                payload: { guideId, isCompleted: false },
            });
        },
        [dispatchGuides]
    );

    const markAllCompleted = useCallback((): void => {
        dispatchGuides({
            type: 'set_all_completed',
            payload: { isCompleted: true },
        });
    }, [dispatchGuides]);

    const resetAllCompletion = useCallback((): void => {
        dispatchGuides({
            type: 'set_all_completed',
            payload: { isCompleted: false },
        });
    }, [dispatchGuides]);

    const markCelebrationOver = useCallback((): void => {
        dispatchGuides({
            type: 'set_celebration_over',
            payload: { isCelebrationOver: true },
        });
    }, [dispatchGuides]);

    const resetCelebrationOver = useCallback((): void => {
        dispatchGuides({
            type: 'set_celebration_over',
            payload: { isCelebrationOver: false },
        });
    }, [dispatchGuides]);

    return {
        ...guidesState,
        openManager,
        closeManager,
        selectGuide,
        startGuide,
        stopGuide,
        finishGuide,
        setStepIndex,
        markDiscovered,
        resetDiscovery,
        markAllDiscovered,
        resetAllDiscovery,
        markCompleted,
        resetCompletion,
        markAllCompleted,
        resetAllCompletion,
        markCelebrationOver,
        resetCelebrationOver,
    };
};

export { useGuideManager };
export type { GuideManagerControls };
